<script setup>
definePage({
  meta: {
    action: 'write',
    subject: 'subject',
    navActiveLink: 'subjects',
  },
})

import { samples, sex, species } from '@/datas/subject'
import { camelCase } from '@/utils/jsutils'
import { isLockedProject } from '@/utils/project'
import { useESignatureStore } from "@/utils/stores/esignature"
import { useSnackBar } from '@/utils/stores/snackbar'
import ConfirmReason from "@/views/build/common/confirmReason.vue"

const esignatureStore = useESignatureStore()
const router = useRouter()
const snackbarStore = useSnackBar()

const id = router.currentRoute.value.params.id

// 데이터 가져오기..
if(await isLockedProject()) {router.back()}
const data = reactive({})

const { data: subject, error } = await useApi(createUrl(`/subjects/${id}`, {
  query: {
  },
}))

subject.value.sample = subject.value.sample ? JSON.parse(subject.value.sample) : []
Object.assign(data, camelCase(subject.value))

const refForm = ref(null)

const years = Array.from({ length: 30 }, (_, i) => i)
const months = Array.from({ length: 12 }, (_, i) => i)
const speciesSelects = species
const sexSelects = sex

const breedSelects = computed(() => {
  return speciesSelects.find(species => {return species.value == data.species}) ?? []
})

watch(() => data.species, () => {
  data.breed = ""
})

const saveSubject = async () => {
  const validate = await refForm?.value.validate()
  if(!validate.valid) return

  esignatureStore.$reset()
  esignatureStore.options ={
    title: "수정 하는 사유를 입력해 주세요.",
    subTitle: "수정 사유",
  }
  esignatureStore.isDialogVisible = true
}

const setConfirm = async ({ result, options }) => {

  if(result) {
    const result = await $api(`/subjects/${id}`, { method: 'PATCH',
      body: { subject: data, reason: options.value.reason } })

    snackbarStore.showSnackBar("Successfully Saved!")
    router.push({ name: `subjects` })
  }
}

const age = computed(() => {
  const today = new Date()
  const birth = new Date(data.birthday)

  let years = today.getFullYear() - birth.getFullYear()
  let months = today.getMonth() - birth.getMonth()

  if (today.getDate() < birth.getDate()) {
    months--
  }

  if (months < 0) {
    years--
    months += 12
  }

  return { years, months }
})

const ageYears = computed({
  get: () => age.value.years,
  set: newYears => {
    const today = new Date()
    const previousDate = new Date(today)

    previousDate.setFullYear(today.getFullYear() - newYears)
    previousDate.setMonth(today.getMonth() - age.value.months)

    data.birthday = previousDate.toISOString().slice(0, 10)
  },
})

const ageMonths = computed({
  get: () => age.value.months,
  set: newMonths => {
    const today = new Date()
    const previousDate = new Date(today)

    previousDate.setFullYear(today.getFullYear() - age.value.years)
    previousDate.setMonth(today.getMonth() - newMonths)

    data.birthday = previousDate.toISOString().slice(0, 10)
  },
})

const birthdayPickerConfig = computed(() => {
  return {
    maxDate: new Date(),
    dateFormat: `Y-m-d`,
  }
})
</script>

<template>
  <ConfirmReason @confirm="setConfirm" />
  <VRow>
    <VCol
      cols="12"
      sm="12"
      md="9"
      lg="7"
      order="1"
      order-lg="1"
    >
      <VCard :title="$t('Subject')">
        <!-- <VCardTitle></VCardTitle> -->
        <VCardText class="mt-4">
          <VForm
            ref="refForm"
            @submit.prevent="() => {}"
          >
            <VRow>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Select Site -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="siteId">{{ $t('Site') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    {{ data.siteName }}
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Consent Date -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="consentDate">{{ $t('Date of informed consent') }}</label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppDateTimePicker
                      id="consentDate"
                      v-model="data.consentDate"
                      append-inner-icon="tabler-calendar-event"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Site Subject Number -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="subjectNo">{{ $t('Site Subject Number') }}</label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VTextField
                      id="subjectNo"
                      v-model="data.subjectNo"
                      persistent-placeholder
                      readonly
                      variant="plain"
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="type">시험 대상<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup
                      v-model="data.type"
                      inline
                    >
                      <VRadio
                        label="동물"
                        :value="0"
                      />
                      <VRadio
                        label="검체"
                        :value="1"
                      />
                    </VRadioGroup>
                  </VCol>
                </VRow>
              </VCol>
              <VCol
                v-if="data.type == 1"
                cols="12"
              >
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  />
                  <VCol
                    class="demo-space-x"
                    cols="12"
                    md="9"
                  >
                    <VCheckbox
                      v-for="(sample, i) in samples"
                      :key="i"
                      v-model="data.sample"
                      :label="sample"
                      :value="sample"
                      :rules="[v => v.length > 0 && data.type == 1 || '적어도 하나 이상을 선택하세요!']"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Subject Name -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="subjectName">{{ $t('Subject Name') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="subjectName"
                      v-model="data.name"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Chart No -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="chartNo">{{ $t('Chart No') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="chartNo"
                      v-model="data.chartNo"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Species -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="species">{{ $t('Species') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppSelect
                      id="species"
                      v-model="data.species"
                      :items="speciesSelects"
                      item-title="value"
                      item-value="value"
                      density="compact"
                      placeholder="Select"
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Breed -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="breed">{{ $t('Breed') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppCombobox
                      id="breed"
                      v-model="data.breed"
                      :items="breedSelects.breed"
                      density="compact"
                      placeholder="Select"
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Age -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="age">{{ $t('Age') }}<span style="color:red">*</span></label>
                  </VCol>
                  <VCol md="3">
                    <AppDateTimePicker
                      :key="JSON.stringify(birthdayPickerConfig)"
                      v-model="data.birthday"
                      class="mr-4"
                      :label="$t('Birthday')"
                      :config="birthdayPickerConfig"
                    />
                  </VCol>
                  <VCol md="3">
                    <VRow>
                      <VCol cols="6">
                        <AppSelect
                          v-model="ageYears"
                          :items="years"
                          label="년"
                        />
                      </VCol>
                      <VCol cols="6">
                        <AppSelect
                          v-model="ageMonths"
                          :items="months"
                          label="개월"
                        />
                      </VCol>
                    </VRow>
                  </VCol>
                </VRow>
              </VCol>

              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Sex -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="sex">{{ $t('Sex') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppSelect
                      v-model="data.sex"
                      :items="sexSelects"
                      item-title="value"
                      item-value="value"
                      density="compact"
                      placeholder="Select"
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <!-- 👉 submit and reset button -->
              <VCol
                offset-md="3"
                cols="12"
                md="9"
                class="d-flex gap-4"
              >
                <VBtn
                  type="submit"
                  @click="saveSubject"
                >
                  {{ $t('Save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  @click="$router.back()"
                >
                  {{ $t('Cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </vcard>
    </VCol>
  </VRow>
</template>
